import { WCCSliderConfig } from 'components/slider/slider.interfaces';
import { wccModules } from 'enums/wccModules';
import { Func } from 'interfaces/func';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import SimpleTopicsManager from 'managers/discussion/simpleTopics';
import { IWCCStorageManager } from 'managers/iStorage';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import CommentsFilters from 'models/commentsFilters';
import { SimpleTopicsGroup } from 'models/simpleTopicsGroup';
import { TopicsSliderGroup } from './group';
import './slider.html';
import './slider.scss';
import { TopicsSliderTopic, TopicsSliderTopicVMConfig, TopicsSliderTopicVMTheme } from './topic';

components.registerLocal('task-topics-slider-group', () => require('components/task/topics/slider/group'));
components.registerLocal('task-topics-slider-topic', () => require('components/task/topics/slider/topic'));
components.registerLocal('task-topics-slider-page-control', () => require('components/task/topics/slider/pageControl'));

components.preload('wcc-slider', 'topic-box');

export type TopicsSliderVMTheme = TopicsSliderTopicVMTheme;

export interface TopicsSliderVMConfig {
    taskId?: SubscribableOrNullableValue<string>
    groupId?: SubscribableOrNullableValue<string>
    activeTopicId?: SubscribableOrNullableValue<string>
    selectedTopicId?: SubscribableOrNullableValue<string>
    filters?: SubscribableOrNullableValue<CommentsFilters>
    theme?: SubscribableOrNullableValue<TopicsSliderVMTheme>

    onTopicClick?: Action<[string]>
    onTopicActivate?: Action<[string]> | Func<Promise<void>, [string]>
}

@injectable()
export default class TopicsSliderVM {
    private pickedGroupItem = ko.observable<TopicsSliderGroup>();
    private allowHighlighting = ko.observable(false);

    private theme: Subscribable<TopicsSliderVMTheme>
    private groupItems: Subscribable<Array<TopicsSliderGroup>>
    private allTopicItems: Subscribable<Array<TopicsSliderTopic>>

    private onTopicClick: Action<[string]>
    private onTopicActivate?: Action<[string]> | Func<Promise<void>, [string]>

    isBusy = ko.observable(false);

    groupsSlider: WCCSliderConfig<TopicsSliderGroup>
    topicsSlider: WCCSliderConfig<TopicsSliderTopic>

    groupsShown: Subscribable<boolean>

    isReady: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) private config: TopicsSliderVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const taskId = ko.flattenComputed(config.taskId);
        const groupId = ko.flattenComputed(config.groupId);
        const activeTopicId = ko.flattenComputed(config.activeTopicId);
        const selectedTopicId = ko.flattenComputed(config.selectedTopicId);        
        const filters = ko.flattenComputed(config.filters);
        const searchString = filters.pluck(f => f.searchString, '');
        const peopleTagsIDs = filters.pluck(f => f.peopleTagsIDs, []);

        this.theme = ko.flattenComputed(config.theme, 'classic');

        this.onTopicClick = config.onTopicClick ?? _.noop;
        this.onTopicActivate = config.onTopicActivate;

        const topicsManager = storage.get(SimpleTopicsManager, { discussionId: taskId, searchString, peopleTagsIDs });
        const groups = ko.observableArray<SimpleTopicsGroup>();
        const cachedGroups = groups.cache('id', (cachedGroup, newGroup) => cachedGroup.update(newGroup.toJson()), group => group.clone()).extend({ deferred: true });
        const filteredGroups = cachedGroups.filter(g => groupId() == undefined ? true : g.id() === groupId());

        this.groupItems = filteredGroups.map(group => new TopicsSliderGroup(group));
        this.groupsShown = filteredGroups.is(groups => groups.length > 1);
        const selectedGroupItem = this.groupItems.find(item => item.isSelected());

        this.allTopicItems = this.groupItems.pluck(item => item.topics).flatten().extend({ trackArrayChanges: true });

        const topicItems = selectedGroupItem.pluck(item => item.topics, []);
        const selectedTopicItem = topicItems.find(item => item.isSelected());

        const selectedTopicIdOrDefault = selectedTopicId.default(topicItems.first().pluck(item => item.topic.id()));
        const activeTopicIdOrDefault = activeTopicId.default(selectedTopicIdOrDefault);

        this.groupsSlider = {
            source: this.groupItems,
            selectedItem: selectedGroupItem,
            showControlsOverContent: false
        };

        let sliderItemMinWidth = 285, sliderItemMaxWidth = 350;

        if (settings.isMaterialUIEnabled()) {
            sliderItemMinWidth = 115;
            sliderItemMaxWidth = 250;
        }

        this.topicsSlider = {
            source: topicItems,
            selectedItem: selectedTopicItem,
            layout: { type: 'dynamic', minWidth: sliderItemMinWidth, maxWidth: sliderItemMaxWidth },
            otherPages: { itemWidth: 0.4 },
            showControlsOverContent: false
        };

        this.isReady = ko.pureComputed(() => cachedGroups().length > 0);

        effects.register([
            selectedTopicId.subscribe(() => this.pickedGroupItem(undefined)),

            this.allTopicItems.subscribe(topics => this.onTopicsChanged(topics)),
            this.allTopicItems.onArrayChange({ add: topic => this.onTopicAdded(topic) }),

            ko.computed(() => {
                const pickedGroupItem = this.pickedGroupItem();

                if (pickedGroupItem == undefined) {
                    if (groupId() != undefined) {
                        this.groupItems().forEach(item => item.isSelected(item.group.id() === groupId()));
                    } else {
                        this.groupItems().forEach(item => item.isSelected(item.containsTopic(<string>selectedTopicId())));
                    }
                } else {
                    this.groupItems().forEach(item => item.isSelected(item == pickedGroupItem));
                }
            }),

            ko.computed(() => topicItems().forEach(item => item.isSelected(item.topic.id() == selectedTopicIdOrDefault()))),
            ko.computed(() => topicItems().forEach(item => item.isActive(item.topic.id() == activeTopicIdOrDefault())))
        ]);

        effects.register((topicsManager) => {
            if (topicsManager)
                return withEffect((newGroups, isLoading) => {
                    if (!isLoading)
                        groups(newGroups);
                }, [topicsManager.groups, topicsManager.loading]);
        }, [topicsManager]);

        ko.applyBindingsToNode(info.element, {
            'visible': this.isReady,

            'css': {
                'task__topics-slider': true,
                'task__topics-slider--no-groups': this.groupsShown.not()
            }
        });
    }

    pickGroup = (groupItem: TopicsSliderGroup) => {
        this.pickedGroupItem(groupItem);
    }

    pickTopic = (topicItem: TopicsSliderTopic) => {
        this.onTopicClick(<string>topicItem.topic.id());
    }

    getTopicConfig = (topicItem: TopicsSliderTopic) => {
        return <TopicsSliderTopicVMConfig>{
            topicItem: topicItem,
            theme: this.theme,

            activate: this.onTopicActivate != undefined ?
                () => system.lockOrSkip(this.isBusy, () => Promise.resolve(this.onTopicActivate?.(<string>topicItem.topic.id()))) :
                undefined
        }
    }

    private onTopicsChanged(topicItems: Array<TopicsSliderTopic>) {
        if (topicItems.length > 0)
            _.defer(() => this.allowHighlighting(true));
    }

    private onTopicAdded(topicItem: TopicsSliderTopic) {
        if (this.allowHighlighting()) {
            const groupItem = this.groupItems().find(item => item.containsTopic(<string>topicItem.topic.id()));

            if (groupItem != undefined) {
                if (groupItem.group.topics().length === 1)
                    groupItem.isHighlighted(true);

                if (topicItem != undefined) {
                    topicItem.isHighlighted(true);

                    _.delay(() => {
                        groupItem.isHighlighted(false);
                        topicItem.isHighlighted(false);
                    }, 5000);
                }
            }
        }
    };
}